import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { MapPinIcon } from '@heroicons/react/20/solid';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import FormSelect from '@ui/form/input/FormSelect';
import FormRadio from '@ui/form/input/FormRadio';
import DataProvider from '@app/model/_common/DataProvider';
import { findProviderData } from '@app/component/provider/FindProviderData';
import { BookingPositionCountry, BookingScanCity } from './model/BookingPositionState';
import { formatCount } from './BookingRanking.utilities';

export type BookingRankingFilterValues = {
	channel: DataProvider,
	country: string,
	device: 'WEB' | 'MOBILE',
};

// The filter bar: Channel (Booking.com first; D4), the read-only Booking city, Guests from (the countries
// that the admin set; D9) and Device (web and mobile; D12). No settings and no filters (D9).
const BookingRankingFilterBar: React.FunctionComponent<{
	form: UseFormReturn<BookingRankingFilterValues>,
	bookingCity: BookingScanCity,
	countries: BookingPositionCountry[],
	mobileHasData: boolean,
	right: React.ReactNode,
}> = ({ form, bookingCity, countries, mobileHasData, right }) =>
	<section className="rounded-lg bg-white px-4 py-4 shadow sm:px-5">
		<div className="flex flex-wrap items-stretch gap-x-2 gap-y-4">
			<CaptionGroup caption="Channel" className="w-full sm:w-auto">
				<div className="w-44">
					<FormSelect form={form} name="channel" size="w-full" options={[{
						id: DataProvider.BOOKING,
						value: DataProvider.BOOKING,
						label: <span className="inline-flex items-center gap-2">{findProviderData(DataProvider.BOOKING).icon}Booking.com</span>,
					}]} />
				</div>
			</CaptionGroup>
			<CaptionGroup caption="Booking city" className="w-full sm:w-auto">
				<span className="inline-flex items-center gap-x-2.5">
					<span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-gray-900">
						<MapPinIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
						{bookingCity.name}
					</span>
					{bookingCity.onSaleCount !== null && <span className="text-xs text-gray-500">
						about {formatCount(bookingCity.onSaleCount)} properties on sale
					</span>}
				</span>
			</CaptionGroup>
			<CaptionGroup caption="Guests from" className="w-full sm:w-auto">
				<span>
					<FormRadio form={form} name="country" options={countries.map((c) => ({ label: c.name, value: c.code }))} />
				</span>
			</CaptionGroup>
			<CaptionGroup caption="Device" className="w-full sm:w-auto">
				<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<FormRadio form={form} name="device" options={[{ label: 'Web', value: 'WEB' }, { label: 'Mobile', value: 'MOBILE' }]} />
					{!mobileHasData && <span className="basis-full text-xs text-gray-500 sm:basis-auto">Mobile from the next scan</span>}
				</span>
			</CaptionGroup>
			<div className="flex items-center gap-2 sm:ml-auto">{right}</div>
		</div>
	</section>;

export default BookingRankingFilterBar;
